import React from 'react';

const INLINE_TOKEN_REGEX = /(!?\[[^\]]+\]\((?:https?:\/\/|mailto:)[^)]+\)|https?:\/\/[^\s<]+|`[^`]+`|\*\*\*[^*]+\*\*\*|___[^_]+___|\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;
const TABLE_SEPARATOR_REGEX = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;

function trimUrl(url) {
  return String(url || '').replace(/[.,;:!?)]$/, '');
}

function renderInlineMarkdown(text) {
  const parts = String(text || '').split(INLINE_TOKEN_REGEX);

  return parts.map((part, index) => {
    if (!part) return null;

    const imageMatch = part.match(/^!\[([^\]]*)\]\(((?:https?:\/\/|mailto:)[^)]+)\)$/);
    if (imageMatch) {
      return <img key={index} src={imageMatch[2]} alt={imageMatch[1]} loading="lazy" />;
    }

    const linkMatch = part.match(/^\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^)]+)\)$/);
    if (linkMatch) {
      return <a key={index} href={linkMatch[2]} target="_blank" rel="noreferrer">{linkMatch[1]}</a>;
    }

    if (/^https?:\/\/[^\s<]+$/.test(part)) {
      const href = trimUrl(part);
      const suffix = part.slice(href.length);
      return (
        <React.Fragment key={index}>
          <a href={href} target="_blank" rel="noreferrer">{href}</a>
          {suffix}
        </React.Fragment>
      );
    }

    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }

    if ((part.startsWith('***') && part.endsWith('***')) || (part.startsWith('___') && part.endsWith('___'))) {
      return <strong key={index}><em>{renderInlineMarkdown(part.slice(3, -3))}</em></strong>;
    }

    if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
      return <strong key={index}>{renderInlineMarkdown(part.slice(2, -2))}</strong>;
    }

    if (part.startsWith('~~') && part.endsWith('~~')) {
      return <del key={index}>{renderInlineMarkdown(part.slice(2, -2))}</del>;
    }

    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
      return <em key={index}>{renderInlineMarkdown(part.slice(1, -1))}</em>;
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function flushParagraph(blocks, paragraph) {
  if (!paragraph.length) return;
  blocks.push({ type: 'paragraph', text: paragraph.join('\n') });
  paragraph.length = 0;
}

function flushList(blocks, list) {
  if (!list.items.length) return;
  blocks.push({ type: 'list', ordered: list.ordered, items: [...list.items] });
  list.items.length = 0;
  list.ordered = false;
}

function flushBlockquote(blocks, blockquote) {
  if (!blockquote.length) return;
  blocks.push({ type: 'blockquote', blocks: parseMarkdown(blockquote.join('\n')) });
  blockquote.length = 0;
}

function flushAll(blocks, paragraph, list, blockquote) {
  flushParagraph(blocks, paragraph);
  flushList(blocks, list);
  flushBlockquote(blocks, blockquote);
}

function splitTableRow(row) {
  return row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function getTableAlignments(separator) {
  return splitTableRow(separator).map((cell) => {
    const starts = cell.startsWith(':');
    const ends = cell.endsWith(':');
    if (starts && ends) return 'center';
    if (ends) return 'right';
    return starts ? 'left' : '';
  });
}

function parseMarkdown(content) {
  const blocks = [];
  const paragraph = [];
  const list = { ordered: false, items: [] };
  const blockquote = [];
  const lines = String(content || '').replace(/\r\n/g, '\n').split('\n');

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index];
    const line = rawLine.trim();

    if (!line) {
      flushAll(blocks, paragraph, list, blockquote);
      continue;
    }

    if (/^```/.test(line) || /^~~~/.test(line)) {
      flushAll(blocks, paragraph, list, blockquote);
      const fence = line.slice(0, 3);
      const language = line.slice(3).trim();
      const code = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith(fence)) {
        code.push(lines[index]);
        index += 1;
      }
      blocks.push({ type: 'code', language, text: code.join('\n') });
      continue;
    }

    if (/^(---+|\*\*\*+|___+)\s*$/.test(line)) {
      flushAll(blocks, paragraph, list, blockquote);
      blocks.push({ type: 'hr' });
      continue;
    }

    const nextLine = lines[index + 1] || '';
    if (line.includes('|') && TABLE_SEPARATOR_REGEX.test(nextLine)) {
      flushAll(blocks, paragraph, list, blockquote);
      const header = splitTableRow(line);
      const alignments = getTableAlignments(nextLine);
      const rows = [];
      index += 2;
      while (index < lines.length && lines[index].trim().includes('|') && lines[index].trim()) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'table', header, alignments, rows });
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      flushAll(blocks, paragraph, list, blockquote);
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2].replace(/\s+#*$/, '') });
      continue;
    }

    const listItem = line.match(/^([-*+])\s+(.+)$/) || line.match(/^(\d+)[.)]\s+(.+)$/);
    if (listItem) {
      flushParagraph(blocks, paragraph);
      flushBlockquote(blocks, blockquote);
      const ordered = /^\d+$/.test(listItem[1]);
      if (list.items.length && list.ordered !== ordered) flushList(blocks, list);
      list.ordered = ordered;
      list.items.push(listItem[2]);
      continue;
    }

    if (line.startsWith('>')) {
      flushParagraph(blocks, paragraph);
      flushList(blocks, list);
      blockquote.push(line.replace(/^>\s?/, ''));
      continue;
    }

    flushList(blocks, list);
    flushBlockquote(blocks, blockquote);
    paragraph.push(line);
  }

  flushAll(blocks, paragraph, list, blockquote);
  return blocks;
}

function renderTextWithBreaks(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {index > 0 && <br />}
      {renderInlineMarkdown(line)}
    </React.Fragment>
  ));
}

function MarkdownBlocks({ blocks }) {
  return blocks.map((block, index) => {
    if (block.type === 'heading') {
      const Tag = `h${Math.min(block.level + 1, 6)}`;
      return <Tag key={index}>{renderInlineMarkdown(block.text)}</Tag>;
    }

    if (block.type === 'list') {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag key={index}>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderTextWithBreaks(item)}</li>
          ))}
        </Tag>
      );
    }

    if (block.type === 'blockquote') {
      return (
        <blockquote key={index}>
          <MarkdownBlocks blocks={block.blocks} />
        </blockquote>
      );
    }

    if (block.type === 'code') {
      return (
        <pre key={index} data-language={block.language || undefined}>
          <code>{block.text}</code>
        </pre>
      );
    }

    if (block.type === 'hr') {
      return <hr key={index} />;
    }

    if (block.type === 'table') {
      return (
        <div key={index} className="js-ama-markdown__table-wrap">
          <table>
            <thead>
              <tr>
                {block.header.map((cell, cellIndex) => (
                  <th key={cellIndex} style={block.alignments[cellIndex] ? { textAlign: block.alignments[cellIndex] } : undefined}>
                    {renderInlineMarkdown(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {block.header.map((_, cellIndex) => (
                    <td key={cellIndex} style={block.alignments[cellIndex] ? { textAlign: block.alignments[cellIndex] } : undefined}>
                      {renderInlineMarkdown(row[cellIndex] || '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <p key={index}>{renderTextWithBreaks(block.text)}</p>;
  });
}

function MarkdownResponse({ content }) {
  const blocks = parseMarkdown(content);

  return (
    <div className="js-ama-markdown">
      <MarkdownBlocks blocks={blocks} />
    </div>
  );
}

export default MarkdownResponse;
